'use client';

import { useRef, useState } from 'react';
import { accordionVariants, defaultVariants, tabButtonVariants } from './TabbedContent.styles';
import { TabbedContentPanel } from './TabbedContentPanel';
import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { getCSLPAttributes } from '@/utils/type-guards';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { ITabbedContentModularBlock } from '@/.generated';
import { Col, Row } from '@/components/primitives/Grid';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { trackAdobeClick } from '@/utils/adobe/click';

type TabbedContentProps = ITabbedContentModularBlock & WithMetadata<IBaseComponentProps>;

type TabItem = NonNullable<ITabbedContentModularBlock['tab_content_items']>[number];

const hasPanelData = (item: TabItem): boolean => {
  if (item.tab_content_type === 'Image') return !!item.image_data?.image?.url;
  return !!item.rte;
};

const Default = (props: TabbedContentProps) => {
  const {
    componentName,
    _metadata,
    tab_variant,
    tab_content_items,
    inline_navigation_id,
    hide_component,
  } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const [openItem, setOpenItem] = useState<number | null>(0);
  const tabListRef = useRef<HTMLUListElement>(null);
  const isDesktop = useMediaQuery('(min-width: 991px)');

  if (hide_component) return null;

  const handleTabChange = (index: number) => {
    if (index === activeIndex) return;
    setActiveIndex(index);
    setPhase('out');
  };

  // after the panel finishes fading out, swap to the selected tab and fade it back in
  const handlePanelAnimationEnd = (e: React.AnimationEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget || phase !== 'out') return;
    setDisplayIndex(activeIndex);
    setPhase('in');
  };

  const visibleItems = (tab_content_items ?? []).filter(hasPanelData);

  if (visibleItems.length === 0) return <></>;

  const orientation = tab_variant === 'Horizontal' ? 'horizontal' : 'vertical';
  const slots = defaultVariants({ orientation });
  const idBase = _metadata?.uid ?? 'tc';

  const toggleAccordionItem = (index: number) => {
    setOpenItem((prev) => (prev === index ? null : index));
  };

  if (!isDesktop) {
    return (
      <Container
        componentName={componentName}
        metaId={_metadata?.uid}
        id={inline_navigation_id}
        horizontalPadding
      >
        <AccordionView
          items={visibleItems}
          openItem={openItem}
          onToggle={toggleAccordionItem}
          idBase={idBase}
          orientation={orientation}
          slots={slots}
        />
      </Container>
    );
  }

  const { base, tabList, panelWrapper } = slots;
  const displayItem = visibleItems[displayIndex] ?? visibleItems[0];

  return (
    <Container
      componentName={componentName}
      metaId={_metadata?.uid}
      id={props.inline_navigation_id}
    >
      <Row className={base()}>
        <Col ml={orientation === 'vertical' ? 5 : 12}>
          <ul className={tabList()} ref={tabListRef} role="tablist" aria-orientation={orientation}>
            {visibleItems.map((item, index) => {
              const isActive = index === activeIndex;
              const {
                item: itemSlot,
                button,
                tabIcon,
                chevron,
              } = tabButtonVariants({ isActive, orientation });
              const tabId = `tab-${idBase}-${index}`;
              const panelId = `tabpanel-${idBase}-${index}`;
              return (
                <li key={index} className={itemSlot()} role="presentation">
                  <button
                    id={tabId}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={panelId}
                    tabIndex={0}
                    className={button()}
                    onClick={(e) => {
                      trackAdobeClick({ clickName: item.tab_title ?? `Tab ${index + 1}` }, e);
                      handleTabChange(index);
                    }}
                    {...getCSLPAttributes(item.$?.tab_title)}
                  >
                    {orientation === 'horizontal' && item.tab_icon?.url && (
                      <ImageWrapper
                        image={toEnhancedImage(item.tab_icon, {
                          alternateText: item.tab_title,
                          fallbackWidth: 95,
                          fallbackHeight: 95,
                          cslp: item.$?.tab_icon,
                        })}
                        imageClassName={tabIcon()}
                      />
                    )}
                    <PlainTextWrapper
                      tag="span"
                      content={item.tab_title}
                      isDangerouslySetInnerHTML
                    />
                    <i className={chevron()} aria-hidden="true"></i>
                  </button>
                </li>
              );
            })}
          </ul>
        </Col>
        <Col
          key={`${displayIndex}-${phase}`}
          ml={orientation === 'vertical' ? 7 : 12}
          className={panelWrapper({ phase })}
          onAnimationEnd={handlePanelAnimationEnd}
        >
          {displayItem && (
            <TabbedContentPanel
              item={displayItem}
              id={`tabpanel-${idBase}-${displayIndex}`}
              labelledBy={`tab-${idBase}-${displayIndex}`}
              slots={slots}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

interface AccordionViewProps {
  items: TabItem[];
  openItem: number | null;
  onToggle: (index: number) => void;
  idBase: string;
  slots: ReturnType<typeof defaultVariants>;
  orientation: 'horizontal' | 'vertical';
}

const AccordionView = ({
  items,
  openItem,
  onToggle,
  idBase,
  slots,
  orientation,
}: AccordionViewProps) => {
  // icons only exist on the horizontal variant; reserve the icon column on every row so titles line up
  const hasIconColumn =
    orientation === 'horizontal' && items.some((item) => Boolean(item.tab_icon?.url));
  const { list, tabIcon, tabTitle } = accordionVariants({ hasIcon: hasIconColumn });

  return (
    <Row>
      <Col>
        <ul className={list()}>
          {items.map((item, index) => {
            const isOpen = openItem === index;
            const { button } = tabButtonVariants({ isActive: isOpen, hasIcon: hasIconColumn });
            const { itemWrapper, contentWrapper, contentInner, panelWrapper } = accordionVariants({
              isOpen,
              hasIcon: hasIconColumn,
            });
            const panelId = `tabpanel-${idBase}-${index}`;
            const headingId = `tab-${idBase}-${index}`;

            return (
              <li key={index} className={itemWrapper()}>
                <button
                  id={headingId}
                  className={button()}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={(e) => {
                    trackAdobeClick({ clickName: item.tab_title ?? `Accordion ${index + 1}` }, e);
                    onToggle(index);
                  }}
                  tabIndex={0}
                  {...getCSLPAttributes(item.$?.tab_title)}
                >
                  {hasIconColumn &&
                    (item.tab_icon?.url ? (
                      <img src={item.tab_icon.url} alt="" className={tabIcon()} />
                    ) : (
                      <span className={tabIcon()} aria-hidden="true" />
                    ))}
                  <PlainTextWrapper
                    tag="span"
                    content={item.tab_title}
                    className={tabTitle()}
                    isDangerouslySetInnerHTML
                  />
                </button>
                <div className={contentWrapper()}>
                  <div className={contentInner()}>
                    <div className={panelWrapper()}>
                      <TabbedContentPanel
                        item={item}
                        id={panelId}
                        labelledBy={headingId}
                        slots={slots}
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </Col>
    </Row>
  );
};

const variants = {
  Default,
};

export const TabbedContent = (props: TabbedContentProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
